import React from 'react';
import type {
  AnchorElementProps,
  ButtonElementProps,
  ButtonProps,
} from './Button.types';
import clsx from 'clsx';
import { IconProps } from '../../Icon/Icon.types';
import { Icon } from '../../Icon';

/**
 * Base CSS class name, used when generating BEM syntax class names
 */
const baseCSSClass = 'hg-button';

/**
 * Restructures the component props (less `children` and `icon*`)  to the final
 * set of props to be included on the root element.
 */
function getRootProps({
  className,
  variant,
  color,
  size,
  ...rest
}: ButtonProps): ButtonProps {
  // Transcludes any props (e.g. `data-testid` and `data-theme`) set on the
  // component
  const rootProps: ButtonProps = { ...rest };

  // Generates the final class name
  rootProps.className = clsx(
    baseCSSClass,
    {
      [`${baseCSSClass}--${variant}`]: variant,
      [`${baseCSSClass}--${color}`]: color,
      [`${baseCSSClass}--${size}`]: size,
    },
    className
  );

  return rootProps;
}

/**
 * Generates the markup for the Icon component
 */
const getIcon = (icon: IconProps, type: string) => (
  <Icon
    {...icon}
    className={clsx(
      `${baseCSSClass}__icon`,
      `${baseCSSClass}__icon--${type}`,
      icon.className
    )}
  />
);

/**
 * Generates the content to be included inside the root element
 */
const getContent = (
  children: React.ReactNode,
  iconLeft: IconProps,
  iconRight: IconProps
) => (
  <>
    {!!iconLeft && getIcon(iconLeft, 'left')}
    {children}
    {!!iconRight && getIcon(iconRight, 'right')}
  </>
);

/**
 * A polymorphic component that can be either an `a` or a `button` element based
 * on whether or not the `href` prop has been assigned.
 */
export const Button = React.forwardRef<
  HTMLAnchorElement | HTMLButtonElement,
  ButtonProps
>(({ children, iconLeft, iconRight, theme, testId, ...rest }, ref) => {
  const rootProps = getRootProps(rest);
  const content = getContent(children, iconLeft, iconRight);
  return rest.href ? (
    <a
      data-theme={theme}
      data-testid={testId}
      ref={ref as React.ForwardedRef<HTMLAnchorElement>}
      {...(rootProps as AnchorElementProps)}
    >
      {content}
    </a>
  ) : (
    // Spicy: https://github.com/jsx-eslint/eslint-plugin-react/issues/1555
    // eslint-disable-next-line react/button-has-type
    <button
      data-theme={theme}
      data-testid={testId}
      ref={ref as React.ForwardedRef<HTMLButtonElement>}
      {...(rootProps as ButtonElementProps)}
    >
      {content}
    </button>
  );
});
